import { IconInfoCircle } from '@tabler/icons-react'
import VisualizationsBasicMap from '../visualization-charts/VisualizationsBasicMap'
import VisualizationsBasicBar from '../visualization-charts/VisualizationsBasicBar'
import VisualizationsBasicHeatmap from '../visualization-charts/VisualizationsBasicHeatmap'
import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../../reducers/store'
import { fetchThreatIntelMalware, selectAdversaries } from '../../../reducers/adversaries'
import { useSelector } from 'react-redux'
import PageSkeleton from '../../skeleton/PageSkeleton'
import {
  mapIntelToAttackBySourceMap,
  mapIntelToAttackByTargetMap,
  mapIntelToAttacksPerMonthHeatmap,
  mapIntelToHeatmapThreatEventsByMonth,
  mapIntelToHeatmapThreatTargetEventsByMonth,
  mapIntelToThreatsByDevice,
  mapIntelToTopSourceCountries,
  mapIntelToTopTargetCountries,
} from '../../../utils/malware-mapper'
import { AttacksHeatmapMapperData, BarChart } from '../../../types/StatTypes'
import VisualizationsPewPewMap from '../visualization-charts/VisualizationsPewPewMap'
import { Tooltip } from '@radix-ui/themes'

interface IVisualizationMalwarePageProps {
  mapData: object
}

function VisualizationMalwarePage(props: IVisualizationMalwarePageProps) {
  const { mapData } = props
  const di = useAppDispatch()
  const { malwareData } = useSelector(selectAdversaries)

  const [intelForAttackByTargetCountry, setIntelForAttackByTargetCountry] = useState<string>('')
  const [intelForAttackBySourceCountry, setIntelForAttackBySourceCountry] = useState<string>('')
  const [intelForThreatsByDevice, setIntelForThreatsByDevice] = useState<BarChart>([])
  const [intelForTopSourceCountries, setIntelForTopSourceCountries] = useState<BarChart>([])
  const [intelForTopTargetCountries, setIntelForTopTargetCountries] = useState<BarChart>([])
  const [intelForHeatmapThreatEventsByMonth, setIntelForHeatmapThreatEventsByMonth] =
    useState<AttacksHeatmapMapperData>({
      yAxis: [],
      xAxis: [],
      heatmapData: [],
    })
  const [intelForHeatmapThreatTargetEventsByMonth, setIntelForHeatmapThreatTargetEventsByMonth] =
    useState<AttacksHeatmapMapperData>({
      yAxis: [],
      xAxis: [],
      heatmapData: [],
    })
  const [intelForAttacksPerMonth, setIntelForAttacksPerMonth] = useState<AttacksHeatmapMapperData>({
    yAxis: [],
    xAxis: [],
    heatmapData: [],
  })

  useEffect(() => {
    di(fetchThreatIntelMalware())
  }, [])

  useEffect(() => {
    if (malwareData) {
      setIntelForAttackByTargetCountry(mapIntelToAttackByTargetMap(malwareData.totalAttackByTargetCountry))
      setIntelForAttackBySourceCountry(mapIntelToAttackBySourceMap(malwareData.totalAttackBySourceCountry))
      setIntelForThreatsByDevice(mapIntelToThreatsByDevice(malwareData.threatsByDevice))
      setIntelForTopSourceCountries(mapIntelToTopSourceCountries(malwareData.topSourceCountries))
      setIntelForTopTargetCountries(mapIntelToTopTargetCountries(malwareData.topTargetCountries))
      setIntelForAttacksPerMonth(mapIntelToAttacksPerMonthHeatmap(malwareData.attacksPerMonth))
      setIntelForHeatmapThreatEventsByMonth(mapIntelToHeatmapThreatEventsByMonth(malwareData.heatmapBySourceCountry))
      setIntelForHeatmapThreatTargetEventsByMonth(
        mapIntelToHeatmapThreatTargetEventsByMonth(malwareData.heatmapByTargetCountry)
      )
    }
  }, [malwareData])

  return (
    <>
      {malwareData && (
        <div className="bg-contentBackground h-full max-w-[2000px] mx-auto">
          <div className="pb-9">
            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Malware Attack Source Country and their Targeted Countries
                </div>
                <Tooltip
                  content={`Click on any country to highlight the nations it has targeted, with a tooltip on each target displaying the volume of attacks. After a country has been selected, press Reset Map in order to make a new selection.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsPewPewMap chartData={malwareData.sourceToTargetPewPew} mapData={mapData} />
            </div>

            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Malware Attacks per Target Country
                </div>
                <Tooltip
                  content={`Visualize the intensity of attacks on each country. Darker shades indicate higher attack volumes. Hover over a country to see the exact number of attacks it has received.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicMap chartData={intelForAttackByTargetCountry} mapData={mapData} />
            </div>

            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Malware Attacks per Source Country
                </div>
                <Tooltip
                  content={`Explore the origins of attacks across the globe. Hover over a country to see the number of times it has attacked other nations, with intensity indicated by color shading.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicMap chartData={intelForAttackBySourceCountry} mapData={mapData} />
            </div>

            <div className="grid grid-cols-12 gap-4 h-auto mb-4">
              <div className="col-span-12 md:col-span-6 bg-cardFill rounded-md border border-mainBorder px-4 py-2">
                <div className="flex">
                  <div className="text-mainText text-lg leading-relaxed font-medium">
                    Malware Attacks per Device Type
                  </div>
                  <Tooltip
                    content={`Compare the number of threats on Android and iOS devices. Each bar represents the total number of threats detected per device on each platform.`}
                    side="top"
                    align="end"
                    className="max-w-[400px]"
                  >
                    <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                  </Tooltip>
                </div>
                <VisualizationsBasicBar
                  chartData={intelForThreatsByDevice}
                  axisTitle="Number of Devices"
                  tooltipTitle="Devices"
                />
              </div>
              <div className="col-span-12 md:col-span-6 bg-cardFill rounded-md border border-mainBorder px-4 py-2">
                <div className="flex">
                  <div className="text-mainText text-lg leading-relaxed font-medium">Malware Attacks per Month</div>
                  <Tooltip
                    content={`Visualize the number of malware events each month over the past 4 years. Use the color intensity to identify spikes and trends in malware activity.`}
                    side="top"
                    align="end"
                    className="max-w-[400px]"
                  >
                    <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                  </Tooltip>
                </div>
                <VisualizationsBasicHeatmap chartData={intelForAttacksPerMonth} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Top Malware Attack Source Countries
                </div>
                <Tooltip
                  content={`See which countries are the top sources of attacks. Hover over a bar for specific attack counts.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicBar
                chartData={intelForTopSourceCountries}
                axisTitle="Threat Events"
                tooltipTitle="Threat Events"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Top Malware Attack Target Countries
                </div>
                <Tooltip
                  content={`See which countries are the top targets of attacks. Hover over a bar for specific attack counts.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicBar
                chartData={intelForTopTargetCountries}
                axisTitle="Threat Events"
                tooltipTitle="Threat Events"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Malware Attack Sources over Time
                </div>
                <Tooltip
                  content={`Track attack patterns over time with this heatmap, showing the amount of times a country has been the source of an attack each month. Darker shades indicate higher activity. Hover over a cell to see the exact attack count.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicHeatmap
                chartData={intelForHeatmapThreatEventsByMonth}
                height={'1200px'}
                logarithmic
              />
            </div>

            <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Malware Attack Targets over Time
                </div>
                <Tooltip
                  content={`Track attack patterns over time with this heatmap, showing the amount of times a country has been targeted each month. Darker shades indicate higher activity. Hover over a cell to see the exact attack count.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicHeatmap
                chartData={intelForHeatmapThreatTargetEventsByMonth}
                height={'3200px'}
                logarithmic
              />
            </div>
          </div>
        </div>
      )}
      {!malwareData && <PageSkeleton />}
    </>
  )
}

export default VisualizationMalwarePage
